import dayjs from 'dayjs';
import type { KeyboardEvent } from 'react';

import { CampaignStatus, type Campaign } from '@/entities/campaign';

export type KeywordPreview = {
  items: string[];
  hiddenCount: number;
};

export type CampaignEndingCountdownTone = 'warning' | 'warning-strong' | 'destructive';

type EndingCalculateParams = {
  end_date?: string;
  status: CampaignStatus;
  referenceDate?: Date;
};

function getCampaignEndingRemainingDays({
  end_date,
  status,
  referenceDate,
}: EndingCalculateParams): number | null {
  if (status !== 'active' || !end_date) return null;

  const remainingDays = dayjs(end_date)
    .startOf('day')
    .diff(dayjs(referenceDate).startOf('day'), 'day');
  if (remainingDays < 1 || remainingDays > 3) return null;

  return remainingDays;
}

export function getCampaignEndingCountdown(params: EndingCalculateParams): string | null {
  const remainingDays = getCampaignEndingRemainingDays(params);
  if (remainingDays == null) return null;

  if (remainingDays === 1) {
    return 'Ends tomorrow';
  }

  return `Ends in ${remainingDays} days`;
}

export function getCampaignEndingCountdownTone(
  params: EndingCalculateParams,
): CampaignEndingCountdownTone | null {
  const remainingDays = getCampaignEndingRemainingDays(params);
  if (remainingDays == null) return null;

  return 'destructive';
}

export function getCampaignEndingTooltip(params: EndingCalculateParams): string | null {
  const remainingDays = getCampaignEndingRemainingDays(params);
  if (remainingDays == null) return null;

  if (remainingDays === 1) {
    return 'Post collection stops tomorrow.';
  }

  return `Post collection stops in ${remainingDays} days.`;
}

export function formatCampaignPeriod(start_date: string, end_date?: string) {
  const startDate = dayjs(start_date).format('MMM D, YYYY');
  if (!end_date) return `${startDate} - Open-ended`;
  return `${startDate} - ${dayjs(end_date).format('MMM D, YYYY')}`;
}

export function getKeywordPreview(
  keywords: Campaign['creator_tracking_keywords'] = [],
): KeywordPreview {
  return {
    items: keywords.slice(0, 2),
    hiddenCount: Math.max(keywords.length - 2, 0),
  };
}

export function handleCardKeyboard(event: KeyboardEvent<HTMLElement>, onOpen: () => void) {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  event.preventDefault();
  onOpen();
}
